import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { UUID } from '@aline/common';
import type { ApolloCache, ApolloClient } from '@apollo/client';
import { useApolloClient } from '@apollo/client/react';
import type { PopoverActions } from '@mui/material';
import { ButtonBase, Checkbox, Popover, Skeleton, TextField, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { UserPlus } from 'lucide-react';
import { alineAvatarSizes, AlineTooltip } from 'src/components';
import { AccountAvatar } from 'src/components/AccountAvatar';
import { UsersAvatarGroup } from 'src/components/generic/UsersAvatarGroup';
import { Apollo } from 'src/gql/generated';
import { useCurrentUserId, useSelectCurrentUser } from 'src/gql/hooks/user';
import { useDispatchNotification } from 'src/helpers/snackbars';
import { useDebouncedValue } from 'src/hooks/utils';
import { makeStyles } from 'tss-react/mui';

type ActivitySubscribersProps = { taskId: UUID } | { workflowId: UUID };
type SubscriberOption = Apollo.ActivitySubscriberOptionFieldsFragment;
type ToggleResult = Apollo.ActivitySubscribersFieldsFragment;
type SubscriberUser = ToggleResult['subscribers'][number];
/** The one checkbox a toggle flips in the cached options list. */
interface OptionPatch {
  subscribed: boolean;
  userId: string;
}

const SUBSCRIBER_ERROR = 'Could not update subscribers. Please try again.';
const EMPTY_SUBSCRIBERS_TOOLTIP =
  'No one is subscribed to notifications. Click to add subscribers.';
/** Placeholder name widths — uneven so loading rows read as names rather than a progress bar. */
const SKELETON_NAME_WIDTHS = ['70%', '52%', '80%', '60%', '45%'];

const LOCK_REASON_LABEL = {
  [Apollo.ActivitySubscriberLockReason.Assignee]: 'Assignee',
} as const satisfies Record<Apollo.ActivitySubscriberLockReason, string>;

function getLockReasonLabel(
  lockReason: Apollo.ActivitySubscriberLockReason | null | undefined,
  isExternal: boolean
): string | null {
  if (!lockReason) return null;
  return isExternal
    ? `External ${LOCK_REASON_LABEL[lockReason].toLowerCase()}`
    : LOCK_REASON_LABEL[lockReason];
}

const useStyles = makeStyles()(theme => ({
  avatars: {
    '&:hover': { backgroundColor: theme.palette.action.hover },
    // Pill hover so the rounded avatar stack keeps a rounded highlight (Linear-style).
    borderRadius: 999,
    display: 'flex',
    padding: theme.spacing(0.5),
  },
  /** Holds the trigger in an active state — above its own hover tint — for as long as the picker is open. */
  avatarsOpen: {
    '&:hover': { backgroundColor: theme.appColors.selectedBackground },
    backgroundColor: theme.appColors.selectedBackground,
  },
  emptyText: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(2),
    textAlign: 'center',
  },
  list: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    overflowY: 'auto',
    padding: theme.spacing(0.5),
  },
  lockLabel: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  name: {
    flex: 1,
    overflow: 'hidden',
    textAlign: 'left',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  paper: {
    // Flex column so the picker body fills it and the list scrolls; height is capped inline on open.
    display: 'flex',
    flexDirection: 'column',
    width: 320,
  },
  pickerRoot: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
  },
  root: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.5),
    justifyContent: 'flex-end',
  },
  row: {
    '&:hover': { backgroundColor: theme.palette.action.hover },
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-start',
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
  search: {
    padding: theme.spacing(1.5, 1.5, 0.5),
  },
  // Same footprint as the small Checkbox so placeholder rows line up with the real ones.
  skeletonCheckbox: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 30,
    justifyContent: 'center',
    width: 30,
  },
  skeletonName: {
    flex: 1,
    minWidth: 0,
  },
  /** Mirrors `row`, minus the hover/press affordances a placeholder shouldn't advertise. */
  skeletonRow: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1),
    width: '100%',
  },
}));

/**
 * Linear-style subscriber control for a Task/Workflow activity thread: the avatar stack of who will be
 * notified. Clicking it opens a searchable picker where anyone can subscribe or unsubscribe anyone,
 * the viewer included.
 */
export const ActivitySubscribers = React.memo(function ActivitySubscribers(
  props: ActivitySubscribersProps
): JSX.Element | null {
  const { classes, cx } = useStyles();
  const isTask = 'taskId' in props;
  const popoverActionRef = useRef<PopoverActions | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [placement, setPlacement] = useState<{ maxHeight: number; openUpward: boolean }>({
    maxHeight: 480,
    openUpward: false,
  });
  const repositionPopover = useCallback(() => popoverActionRef.current?.updatePosition(), []);

  /**
   * Measure once, on open: open toward whichever side has more room and cap the height to it so a long
   * list is never clipped. Doing it here (not in render) avoids re-measuring, which could flip the
   * direction mid-open on an unrelated re-render after the page scrolled.
   */
  const openPicker = (event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = rect.top > spaceBelow;
    const room = openUpward ? rect.top : spaceBelow;
    setPlacement({ maxHeight: Math.max(160, Math.min(480, room - 16)), openUpward });
    setAnchorEl(event.currentTarget);
  };

  // Read through the cache but revalidate — assignee/comment changes elsewhere alter these fields.
  const taskQuery = Apollo.useGetTaskSubscribersQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isTask,
    variables: { taskId: isTask ? props.taskId : '' },
  });
  const workflowQuery = Apollo.useGetWorkflowSubscribersQuery({
    fetchPolicy: 'cache-and-network',
    skip: isTask,
    variables: { workflowId: isTask ? '' : props.workflowId },
  });
  const entity = isTask ? taskQuery.data?.task : workflowQuery.data?.workflow;
  if (!entity) return null;

  const pickerOpen = Boolean(anchorEl);
  return (
    <div className={classes.root}>
      <AlineTooltip
        arrow
        placement="top"
        title={entity.subscribers.length ? '' : EMPTY_SUBSCRIBERS_TOOLTIP}
      >
        <ButtonBase
          aria-label="Manage subscribers"
          className={cx(classes.avatars, pickerOpen && classes.avatarsOpen)}
          onClick={openPicker}
        >
          <SubscriberTriggerContent subscribers={entity.subscribers} />
        </ButtonBase>
      </AlineTooltip>
      <Popover
        action={popoverActionRef}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: 'right', vertical: placement.openUpward ? 'top' : 'bottom' }}
        open={pickerOpen}
        slotProps={{
          paper: { className: classes.paper, style: { maxHeight: placement.maxHeight } },
        }}
        transformOrigin={{ horizontal: 'right', vertical: placement.openUpward ? 'bottom' : 'top' }}
        onClose={() => setAnchorEl(null)}
      >
        <SubscriberPicker parent={props} onContentResize={repositionPopover} />
      </Popover>
    </div>
  );
});

/**
 * Contents of the single, always-mounted picker trigger. A separate button per state would unmount
 * the node an open picker is anchored to the moment the first subscriber is added (or the last one
 * removed), stranding the popover in the corner.
 */
function SubscriberTriggerContent({ subscribers }: { subscribers: SubscriberUser[] }): JSX.Element {
  const viewerOrgId = useSelectCurrentUser()?.org.id ?? null;
  if (!subscribers.length) return <UserPlus size={16} />;

  return (
    <UsersAvatarGroup
      getTooltipTitle={user => getSubscriberTooltip(user, user.org.id !== viewerOrgId)}
      max={4}
      tooltipPlacement="top"
      users={subscribers}
    />
  );
}

interface SubscriberPickerModel {
  query: string;
  rows: readonly SubscriberOption[];
  setQuery: (value: string) => void;
  showSkeleton: boolean;
  toggle: (option: SubscriberOption) => Promise<void>;
}

/**
 * Picker data + actions. Checkbox state comes from the Apollo cache; mutations write optimistic
 * avatar + options updates so toggles don't flicker waiting on the network.
 */
function useSubscriberPicker(
  parent: ActivitySubscribersProps,
  onContentResize: () => void
): SubscriberPickerModel {
  const isTask = 'taskId' in parent;
  const client = useApolloClient();
  const viewerId = useCurrentUserId();
  const [query, setQuery] = useState('');
  // Ignore double-clicks on the same row without disabling the whole list.
  const inFlightIdsRef = useRef(new Set<string>());
  // Search server-side (bounded roster); debounce so we don't refetch on every keystroke.
  const search = useDebouncedValue(query.trim(), 200) || null;
  const taskQuery = Apollo.useGetTaskSubscriberOptionsQuery({
    fetchPolicy: 'cache-and-network',
    skip: !isTask,
    variables: { search, taskId: isTask ? parent.taskId : '' },
  });
  const workflowQuery = Apollo.useGetWorkflowSubscriberOptionsQuery({
    fetchPolicy: 'cache-and-network',
    skip: isTask,
    variables: { search, workflowId: isTask ? '' : parent.workflowId },
  });

  const [subscribe] = Apollo.useSubscribeToActivityMutation();
  const [unsubscribe] = Apollo.useUnsubscribeFromActivityMutation();
  const dispatchError = useDispatchNotification({ message: SUBSCRIBER_ERROR, variant: 'error' });

  const loading = isTask ? taskQuery.loading : workflowQuery.loading;
  // previousData keeps the current rows during a search/refetch instead of flashing empty.
  const options = isTask
    ? (taskQuery.data ?? taskQuery.previousData)?.task?.subscriberOptions
    : (workflowQuery.data ?? workflowQuery.previousData)?.workflow?.subscriberOptions;
  const rows = options ?? [];
  const showSkeleton = loading && !options;

  // The list loads async and grows the paper — reposition once its real height is known.
  useEffect(() => {
    onContentResize();
  }, [onContentResize, rows.length, showSkeleton]);

  const entityInput = isTask ? { taskId: parent.taskId } : { workflowId: parent.workflowId };

  const runToggle = async ({
    nextSubscribed,
    user,
  }: {
    nextSubscribed: boolean;
    user: SubscriberUser;
  }) => {
    if (!viewerId || inFlightIdsRef.current.has(user.id)) return;
    const current = readAvatarSubscribers(client, parent);
    if (!current) return;

    const optimistic = buildToggleResult({
      current,
      nextSubscribed,
      user,
      viewerId,
    });
    const optionPatch = { subscribed: nextSubscribed, userId: user.id };
    const variables = { input: { ...entityInput, userId: user.id } };
    inFlightIdsRef.current.add(user.id);

    try {
      if (nextSubscribed) {
        const { data, error } = await subscribe({
          optimisticResponse: { __typename: 'Mutation', subscribeToActivity: optimistic },
          update: (cache, { data: resultData }) => {
            const result = resultData?.subscribeToActivity;
            if (!result) return;
            applyToggleResultToCache({ cache, optionPatch, parent, result, search });
          },
          variables,
        });
        if (error || !data?.subscribeToActivity) dispatchError();
      } else {
        const { data, error } = await unsubscribe({
          optimisticResponse: { __typename: 'Mutation', unsubscribeFromActivity: optimistic },
          update: (cache, { data: resultData }) => {
            const result = resultData?.unsubscribeFromActivity;
            if (!result) return;
            applyToggleResultToCache({ cache, optionPatch, parent, result, search });
          },
          variables,
        });
        if (error || !data?.unsubscribeFromActivity) dispatchError();
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { subscriber: 'toggle' } });
      dispatchError();
    } finally {
      inFlightIdsRef.current.delete(user.id);
    }
  };

  const toggle = async (option: SubscriberOption) => {
    if (option.lockReason) return;
    await runToggle({ nextSubscribed: !option.subscribed, user: option.user });
  };

  return {
    query,
    rows,
    setQuery,
    showSkeleton,
    toggle,
  };
}

/** The popover body: search box over active org members with a subscribe/unsubscribe checkbox each. */
function SubscriberPicker({
  onContentResize,
  parent,
}: {
  onContentResize: () => void;
  parent: ActivitySubscribersProps;
}): JSX.Element {
  const { classes } = useStyles();
  const viewerOrgId = useSelectCurrentUser()?.org.id ?? null;
  const { query, rows, setQuery, showSkeleton, toggle } = useSubscriberPicker(
    parent,
    onContentResize
  );

  return (
    <div className={classes.pickerRoot}>
      <div className={classes.search}>
        <TextField
          autoFocus
          fullWidth
          placeholder="Search subscribers…"
          size="small"
          value={query}
          variant="standard"
          onChange={event => setQuery(event.target.value)}
        />
      </div>
      {showSkeleton ? (
        <SubscriberRowsSkeleton />
      ) : rows.length === 0 ? (
        <Typography className={classes.emptyText} variant="body2">
          No people found.
        </Typography>
      ) : (
        <div className={classes.list}>
          {rows.map(option => {
            const lockLabel = getLockReasonLabel(
              option.lockReason,
              option.user.org.id !== viewerOrgId
            );

            return (
              <ButtonBase
                key={option.user.id}
                className={classes.row}
                disabled={Boolean(option.lockReason)}
                onClick={() => void toggle(option)}
              >
                <Checkbox
                  disableRipple
                  checked={option.subscribed}
                  disabled={Boolean(option.lockReason)}
                  size="small"
                />
                <AccountAvatar account={option.user} size="sm" />
                <Typography className={classes.name} variant="body2">
                  {option.user.displayName}
                </Typography>
                {lockLabel && (
                  <Typography className={classes.lockLabel} variant="caption">
                    {lockLabel}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Placeholder rows shaped like the real list, so the popover doesn't resize once options land. */
function SubscriberRowsSkeleton(): JSX.Element {
  const { classes } = useStyles();
  const avatarSize = alineAvatarSizes.sm.size;

  return (
    <div className={classes.list}>
      {SKELETON_NAME_WIDTHS.map((width, index) => (
        <div key={index} className={classes.skeletonRow}>
          <span className={classes.skeletonCheckbox}>
            <Skeleton height={18} variant="rounded" width={18} />
          </span>
          <Skeleton height={avatarSize} variant="circular" width={avatarSize} />
          {/* Percentage widths resolve against the leftover space, not the whole row. */}
          <div className={classes.skeletonName}>
            <Skeleton height={18} variant="text" width={width} />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The avatar stack doubles as the entry point to the subscriber picker, so name-only tooltips left
 * that unsaid — spell out both what the avatar means and that clicking it manages subscribers.
 */
function getSubscriberTooltip(
  user: Pick<SubscriberUser, 'displayName'>,
  isExternal: boolean
): string {
  if (isExternal)
    return `${user.displayName} is external and is notified of External activity while assigned.`;
  return `${user.displayName} is subscribed to notifications. Click to change.`;
}

function buildToggleResult({
  current,
  nextSubscribed,
  user,
  viewerId,
}: {
  current: readonly SubscriberUser[];
  nextSubscribed: boolean;
  user: SubscriberUser;
  viewerId: string;
}): ToggleResult {
  const withoutUser = current.filter(subscriber => subscriber.id !== user.id);
  const subscribers = nextSubscribed ? [...withoutUser, user] : withoutUser;
  return {
    __typename: 'ActivitySubscriptionToggleResult',
    subscribers,
    viewerSubscribed: subscribers.some(subscriber => subscriber.id === viewerId),
  };
}

function readAvatarSubscribers(
  client: ApolloClient,
  parent: ActivitySubscribersProps
): SubscriberUser[] | null {
  if ('taskId' in parent) {
    const data = client.readQuery<
      Apollo.GetTaskSubscribersQuery,
      Apollo.GetTaskSubscribersQueryVariables
    >({ query: Apollo.GetTaskSubscribersDocument, variables: { taskId: parent.taskId } });
    return data?.task?.subscribers ?? null;
  }
  const data = client.readQuery<
    Apollo.GetWorkflowSubscribersQuery,
    Apollo.GetWorkflowSubscribersQueryVariables
  >({ query: Apollo.GetWorkflowSubscribersDocument, variables: { workflowId: parent.workflowId } });
  return data?.workflow?.subscribers ?? null;
}

/**
 * Write a toggle result into the avatar subscribers query and patch subscribed flags on the current
 * options query (row order unchanged — avoids list jump/flicker on click).
 */
function applyToggleResultToCache({
  cache,
  optionPatch,
  parent,
  result,
  search,
}: {
  cache: ApolloCache;
  optionPatch: OptionPatch;
  parent: ActivitySubscribersProps;
  result: ToggleResult;
  search: string | null;
}): void {
  const { subscribers, viewerSubscribed } = result;
  if ('taskId' in parent) {
    cache.updateQuery<Apollo.GetTaskSubscribersQuery, Apollo.GetTaskSubscribersQueryVariables>(
      { query: Apollo.GetTaskSubscribersDocument, variables: { taskId: parent.taskId } },
      prev =>
        prev?.task ? { ...prev, task: { ...prev.task, subscribers, viewerSubscribed } } : prev
    );
    cache.updateQuery<
      Apollo.GetTaskSubscriberOptionsQuery,
      Apollo.GetTaskSubscriberOptionsQueryVariables
    >(
      {
        query: Apollo.GetTaskSubscriberOptionsDocument,
        variables: { search, taskId: parent.taskId },
      },
      prev =>
        prev?.task
          ? {
              ...prev,
              task: {
                ...prev.task,
                subscriberOptions: patchOptionSubscribed(prev.task.subscriberOptions, optionPatch),
              },
            }
          : prev
    );
    return;
  }

  cache.updateQuery<
    Apollo.GetWorkflowSubscribersQuery,
    Apollo.GetWorkflowSubscribersQueryVariables
  >(
    { query: Apollo.GetWorkflowSubscribersDocument, variables: { workflowId: parent.workflowId } },
    prev =>
      prev?.workflow
        ? { ...prev, workflow: { ...prev.workflow, subscribers, viewerSubscribed } }
        : prev
  );
  cache.updateQuery<
    Apollo.GetWorkflowSubscriberOptionsQuery,
    Apollo.GetWorkflowSubscriberOptionsQueryVariables
  >(
    {
      query: Apollo.GetWorkflowSubscriberOptionsDocument,
      variables: { search, workflowId: parent.workflowId },
    },
    prev =>
      prev?.workflow
        ? {
            ...prev,
            workflow: {
              ...prev.workflow,
              subscriberOptions: patchOptionSubscribed(
                prev.workflow.subscriberOptions,
                optionPatch
              ),
            },
          }
        : prev
  );
}

function patchOptionSubscribed(
  options: readonly SubscriberOption[],
  { subscribed, userId }: OptionPatch
): SubscriberOption[] {
  return options.map(option =>
    option.user.id === userId && option.subscribed !== subscribed
      ? { ...option, subscribed }
      : option
  );
}
